import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowDown, Check, X, Zap, HelpCircle } from "lucide-react";
import { LIVE_SCENARIOS } from "@/lib/liveOps";

const ATTRIBUTION_COLORS = {
  "Assisted": "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  "Human Assisted": "text-sky-400 border-sky-500/40 bg-sky-500/10",
  "Organic": "text-amber-400 border-amber-500/40 bg-amber-500/10",
  "Excluded": "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
};

const WhyBlock = ({ title, items, testId }) => (
  <div className="ml-[76px] my-2 border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3.5 fade-slide-in" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] font-mono tracking-widest text-emerald-400 uppercase mb-2">
      <HelpCircle className="w-3 h-3" strokeWidth={1.5} /> {title}
    </div>
    <div className="space-y-1">
      {items.map((w) => (
        <div key={w} className="flex items-start gap-2 text-[11px] text-zinc-300">
          <Zap className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" strokeWidth={1.5} /> {w}
        </div>
      ))}
    </div>
  </div>
);

export default function JourneyView() {
  const [params] = useSearchParams();
  const initial = params.get("client") || "ftd";
  const [caseId, setCaseId] = useState(LIVE_SCENARIOS.some((s) => s.id === initial) ? initial : "ftd");
  const sc = useMemo(() => LIVE_SCENARIOS.find((s) => s.id === caseId), [caseId]);

  const items = useMemo(() => {
    const out = [];
    let whyAdded = false, policyAdded = false, decisionAdded = false, channelAdded = false;
    for (const e of sc.timeline) {
      out.push({ kind: "event", e });
      if (e.phase === "state" && !whyAdded) { out.push({ kind: "why" }); whyAdded = true; }
      if (e.phase === "policy" && !policyAdded) { out.push({ kind: "policy" }); policyAdded = true; }
      if (e.phase === "decision" && !decisionAdded) { out.push({ kind: "decision" }); decisionAdded = true; }
      if (e.phase === "action" && !channelAdded && sc.action.kind === "automated") { out.push({ kind: "channel" }); channelAdded = true; }
    }
    return out;
  }, [sc]);

  return (
    <div className="space-y-5" data-testid="journey-view">
      <div>
        <div className="text-[10px] font-mono tracking-widest text-emerald-500 uppercase mb-1">Client Journey View · Simulated demo data</div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">What exactly happened to this client</h1>
        <p className="text-sm text-zinc-400 mt-1">The complete chronological story — first signal to final attribution.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="journey-clients">
        {LIVE_SCENARIOS.map((s) => (
          <button
            key={s.id}
            data-testid={`journey-tab-${s.id}`}
            onClick={() => setCaseId(s.id)}
            className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors duration-200 ${
              caseId === s.id ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/50" : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-600 hover:text-zinc-200"
            }`}
          >
            {s.client.name}
          </button>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6" data-testid="journey-story">
        <div className="flex flex-wrap items-center gap-3 pb-5 border-b border-zinc-800 mb-5">
          <span className="text-lg font-bold text-zinc-100 font-display">{sc.client.name.toUpperCase()}</span>
          <span className="font-mono text-xs text-zinc-500">{sc.client.ref}</span>
          <span className="text-xs text-zinc-400">{sc.client.country} · {sc.client.language} · {sc.client.jurisdiction}</span>
          <span className={`ml-auto px-2.5 py-0.5 rounded-full text-[10px] font-mono border ${ATTRIBUTION_COLORS[sc.attribution]}`} data-testid="journey-attribution">
            Attribution: {sc.attribution}
          </span>
        </div>

        {items.map((item, i) => {
          if (item.kind === "why") {
            return <WhyBlock key="why" title={`Why ${sc.condition.label}?`} items={sc.why} testId="journey-why" />;
          }
          if (item.kind === "policy") {
            return (
              <div key="policy" className="ml-[76px] my-2 border border-sky-500/30 bg-sky-500/5 rounded-sm p-3.5 fade-slide-in" data-testid="journey-policy">
                <div className="text-[10px] font-mono tracking-widest text-sky-400 uppercase mb-2">Policy checks</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                  {sc.policy.checks.map((c, j) => {
                    const failed = c.includes("FAILED");
                    return (
                      <div key={j} className="flex items-start gap-1.5 text-[11px]">
                        {failed ? <X className="w-3 h-3 text-red-400 mt-0.5 shrink-0" /> : <Check className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />}
                        <span className={failed ? "text-red-400" : "text-zinc-400"}>{c.replace(" — FAILED", "")}</span>
                      </div>
                    );
                  })}
                </div>
                {sc.policy.blockReason && <div className="text-[11px] text-red-400 font-semibold mt-2">Why automation blocked: {sc.policy.blockReason}</div>}
              </div>
            );
          }
          if (item.kind === "decision") {
            return (
              <div key="decision" className="ml-[76px] my-2 text-[11px] text-zinc-400 fade-slide-in" data-testid="journey-decision-why">
                <span className="text-emerald-500 font-mono uppercase tracking-wider text-[10px]">Why this decision: </span>
                {sc.decision.reason}
              </div>
            );
          }
          if (item.kind === "channel") {
            return (
              <div key="channel" className="ml-[76px] my-2 text-[11px] text-zinc-400 fade-slide-in" data-testid="journey-channel-why">
                <span className="text-emerald-500 font-mono uppercase tracking-wider text-[10px]">Why this channel &amp; language: </span>
                {sc.action.channel} is the client's best-performing permitted channel; {sc.action.language} comes from the client profile via approved template {sc.action.template}.
              </div>
            );
          }
          const e = item.e;
          return (
            <div key={i}>
              <div className="flex items-start gap-3">
                <span className="font-mono text-[11px] text-zinc-400 mt-0.5 w-16 shrink-0">{e.t}</span>
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${e.breach ? "bg-red-400" : e.phase === "outcome" ? "bg-emerald-400" : "bg-zinc-600"}`} />
                <div className="min-w-0">
                  <div className={`text-xs leading-relaxed ${e.breach ? "text-red-400 font-semibold" : "text-zinc-200"}`}>{e.text}</div>
                  <div className="text-[9px] font-mono text-zinc-600">{e.system}</div>
                </div>
              </div>
              {i < items.length - 1 && <div className="pl-[76px] py-0.5"><ArrowDown className="w-3 h-3 text-zinc-700" strokeWidth={1.5} /></div>}
            </div>
          );
        })}

        <div className="mt-5 border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-4" data-testid="journey-outcome">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-sm font-bold text-emerald-400 font-display">{sc.outcome.title}</span>
            <span className="font-mono text-lg font-bold text-zinc-100">{sc.outcome.value}</span>
            <span className="text-[11px] font-mono text-zinc-500">{sc.outcome.classification}</span>
          </div>
          <div className="text-[11px] text-zinc-400 mt-2">
            <span className="text-emerald-500 font-mono uppercase tracking-wider text-[10px]">Why attributed this way: </span>
            {sc.outcome.attribution}
          </div>
        </div>
      </div>
    </div>
  );
}
